import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { BdShareablesService } from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  SHAREABLE_TYPE_DISPLAY_NAME,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { TableModule } from 'primeng/table';

import { BdUiProgressIndicatorComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import { catchError, finalize, forkJoin, from, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { SelectedItem } from '../../dialog.models';

type CheckStatus = 'pending' | 'running' | 'ok' | 'error';

interface PreviewItem {
  id: string;
  type: ShareableType;
  displayName: string;
  typeLabel: string;
  checkStatus: CheckStatus;
  checkDetail: string;
}

@Component({
  selector: 'bd-move-preview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-4 py-4">
      <p-table
        [value]="previewItems()"
        [scrollable]="true"
        scrollHeight="300px"
      >
        <!--
          The indicator in its compact mode ("Kompaktvariante — Tabellen- und
          Kopfzeilen") rather than the full 1a card: this step is a dense
          confirmation list, so the progress belongs in the caption of the table
          it summarises instead of as a block above it. Failures stay visible as
          a red share of the same track.

          Keyed off the checks that have landed rather than the analysisFinished
          flag: a target folder without write access marks every item failed
          while leaving that flag false, and that outcome still needs a summary.

          Rendered unconditionally. Guarding it would leave PrimeNG drawing an
          empty caption band, and moving the guard out to the ng-template is not
          an option — a PrimeNG template behind @if is never picked up by its
          ContentChild query.
        -->
        <ng-template #caption>
          <bd-ui-progress-indicator
            mode="compact"
            [state]="analysisRunning() ? 'running' : 'done'"
            title="Berechtigungsprüfung"
            [completedCount]="checkedCount()"
            [totalCount]="previewItems().length"
            [errorCount]="failedCount()"
          ></bd-ui-progress-indicator>
        </ng-template>
        <ng-template #header>
          <tr>
            <th>Ressource</th>
            <th>Berechtigung</th>
          </tr>
        </ng-template>
        <ng-template #body let-item>
          <tr>
            <td>
              <div class="flex flex-col">
                <span>{{ item.displayName }}</span>
                <span class="text-xs text-muted-color">{{
                  item.typeLabel
                }}</span>
              </div>
            </td>
            <td>
              @switch (item.checkStatus) {
                @case ('pending') {
                  <i class="pi pi-ellipsis-h text-surface-400"></i>
                }
                @case ('running') {
                  <i
                    class="pi pi-spin pi-spinner"
                    style="color: var(--p-primary-color)"
                  ></i>
                }
                @case ('ok') {
                  <!--
                    One line instead of the former "OK" heading plus a two-item
                    bullet list. A row only reaches 'ok' once BOTH checks passed
                    (the target folder gate in _runAnalysis and the per-item
                    check), so naming them inline loses nothing.
                  -->
                  <span
                    class="flex items-center gap-1 whitespace-nowrap"
                    style="color: var(--p-bd-status-success-color)"
                  >
                    <i class="pi pi-check"></i>
                    OK - Zielordner &amp; Element
                  </span>
                }
                @case ('error') {
                  <span
                    class="flex items-center gap-1"
                    style="color: var(--p-bd-status-error-color)"
                  >
                    <i class="pi pi-times"></i>
                    {{ item.checkDetail }}
                  </span>
                }
              }
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  `,
  imports: [TableModule, BdUiProgressIndicatorComponent],
})
export class BdMovePreviewComponent implements OnInit {
  public readonly items = input.required<SelectedItem[]>();
  public readonly targetFolderId = input.required<string>();

  public readonly analysisFinishedChange = output<boolean>();

  public readonly previewItems = signal<PreviewItem[]>([]);
  public readonly analysisRunning = signal(false);
  public readonly analysisFinished = signal(false);

  /** Checks that have produced a verdict, either way. */
  public readonly checkedCount = computed(
    () =>
      this.previewItems().filter(
        (item) => item.checkStatus === 'ok' || item.checkStatus === 'error',
      ).length,
  );

  public readonly failedCount = computed(
    () =>
      this.previewItems().filter((item) => item.checkStatus === 'error').length,
  );

  private _emitAnalysisFinished(finished: boolean): void {
    this.analysisFinished.set(finished);
    this.analysisFinishedChange.emit(finished);
  }

  private readonly _resourcesService = inject(BdShareablesService);

  ngOnInit(): void {
    this._buildPreviewItems(this.items());
    this._runAnalysis(this.targetFolderId());
  }

  private _buildPreviewItems(items: SelectedItem[]): void {
    this.previewItems.set(
      items.map((item) => ({
        id: item.id,
        type: item.type,
        displayName: item.displayName,
        typeLabel: SHAREABLE_TYPE_DISPLAY_NAME[item.type] ?? item.typeLabel,
        checkStatus: 'pending' as CheckStatus,
        checkDetail: '',
      })),
    );
  }

  private _runAnalysis(folderId: string): void {
    this.analysisRunning.set(true);
    this._emitAnalysisFinished(false);

    from(
      this._resourcesService.hasPermission(
        ShareableType.FOLDER,
        folderId,
        'UPDATE',
      ),
    )
      .pipe(catchError(() => of(false)))
      .subscribe((hasUpdate) => {
        if (!hasUpdate) {
          this.previewItems.update((items) =>
            items.map((it) => ({
              ...it,
              checkStatus: 'error' as CheckStatus,
              checkDetail: 'Kein Schreibzugriff auf Zielordner',
            })),
          );
          this.analysisRunning.set(false);
          this._emitAnalysisFinished(false);
          return;
        }
        this._runItemChecks();
      });
  }

  private _runItemChecks(): void {
    this.previewItems.update((items) =>
      items.map((it) => ({ ...it, checkStatus: 'running' as CheckStatus })),
    );

    const items = this.previewItems();
    if (items.length === 0) {
      this.analysisRunning.set(false);
      this._emitAnalysisFinished(true);
      return;
    }

    const checks: Observable<void>[] = items.map((item, idx) =>
      from(
        this._resourcesService.hasPermission(item.type, item.id, 'UPDATE'),
      ).pipe(
        map((ok) => {
          this.previewItems.update((current) => {
            const next = [...current];
            next[idx] = {
              ...next[idx],
              checkStatus: ok ? 'ok' : 'error',
              checkDetail: ok ? '' : 'Keine Berechtigung',
            };
            return next;
          });
        }),
        catchError(() => {
          this.previewItems.update((current) => {
            const next = [...current];
            next[idx] = {
              ...next[idx],
              checkStatus: 'error',
              checkDetail: 'Prüfung fehlgeschlagen',
            };
            return next;
          });
          return of(undefined);
        }),
      ),
    );

    forkJoin(checks)
      .pipe(finalize(() => this.analysisRunning.set(false)))
      .subscribe(() => {
        const allOk = this.previewItems().every(
          (item) => item.checkStatus === 'ok',
        );
        this._emitAnalysisFinished(allOk);
      });
  }
}
